import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient, useMutation } from "react-query";
import Swal from "sweetalert2";
import { Form, InputGroup } from "react-bootstrap";

import {
  BatteryFullIcon,
  BatteryHalfIcon,
  BatteryIcon,
  EditIcon,
  TrashIcon,
  NoWifi,
  BatteryWarningIcon,
  QuestionMarkIcon,
} from "@components/icons";
import { deleteSensor } from "../services/sensorCrud";
import ReactTable from "@components/ReactTable";
// import StatusButton from "../../Trees/components/Button/StatusButton";
import { GREEN, YELLOW, RED, GREY } from "@helper/colors";
import Header from "@components/ListHeader/ListHeader";

// export function healthHeader(t) {
//   return {
//     Header: `${t("health_state")}`,
//     accessor: "health_state",
//     style: { width: "30px", textAlign: "center", paddingRight: "0" },
//     Cell: (props) => {
//       const [healthState] = useChoices("dry_state");

//       const health_state = healthState?.filter(
//         (item) => item.key == props.row.original.pulse_sensor?.health_state
//       );
//       let treeHealth = health_state && health_state[0] && health_state[0].value;
//       console.log(treeHealth, health_state, healthState, props);
//       return (
//         <>
//           {/* {props.row.original.health_state && ( */}
//           <StatusButton
//             status={props.row.original.pulse_sensor?.health_state}
//             width="fit-content"
//             padding="1px 10px"
//           >
//             {treeHealth ? `${t(treeHealth.replace(/\s/g, ""))}` : ""}
//           </StatusButton>
//           {/* )} */}
//         </>
//       );
//     },
//   };
// }
export function statusHeader(t) {
  return {
    // Header: `${t("state")}`,
    Header: (
      <Header
        name={`${t("status")}`}
        infoTitle={t("sensor_status")}
        infoContent={[
          t("sensor_status_description"),
          <div key="working">
            <SensorStatus color={GREEN} width="15px" title={t("working")} /> - {t("working")}
          </div>,
          <div key="battery_empty">
            <BatteryWarningIcon color={RED} width="15px" title={t("battery_empty")} /> -{" "}
            {t("battery_empty")}
          </div>,
          <div key="maintenance_needed">
            <span title={t("maintenance_needed")} style={{ fontSize: "15px" }}>
              🔧
            </span>{" "}
            - {t("maintenance_needed")}
          </div>,
          <div key="not_sending">
            <NoWifi color={GREY} width="15px" title={t("not_sending")} /> - {t("not_sending")}
          </div>,
          <div key="unknown">
            <QuestionMarkIcon color={GREY} width="15px" title={t("unknown")} /> - {t("unknown")}
          </div>,
        ]}
      />
    ),
    accessor: "sensor_state",
    style: { width: "30px", textAlign: "center", paddingRight: "0" },
    Cell: (props) => {
      const hardwareSerial = props.row.original.hardware_serial;
      let isSendingObj;
      try {
        isSendingObj = JSON.parse(localStorage.getItem("isSending"));
      } catch (e) {
        isSendingObj = null;
      }
      const isSendingSensor = isSendingObj && isSendingObj[hardwareSerial] ? true : false;
      return (
        <>
          {
            {
              0: <SensorStatus color={GREEN} width="15px" title={t("working")} />, //working
              1: <BatteryWarningIcon color={RED} width="15px" title={t("battery_empty")} />, //Battery Empty
              2: (
                <span title={t("maintenance_needed")} style={{ fontSize: "15px" }}>
                  🔧
                </span>
              ), //Maintenance Needed
              3: isSendingSensor ? (
                <SensorStatus color={GREEN} width="15px" title={t("working")} />
              ) : (
                <NoWifi color={GREY} width="15px" title={t("not_sending")} />
              ), //Not Sending
              4: <QuestionMarkIcon color={GREY} width="15px" title={t("unknown")} />,
            }[props.row.original.sensor_state]
          }
        </>
      );
    },
  };
}

export function batteryHeader(t) {
  return {
    Header: `${t("last_battery")}`,
    accessor: "battery",
    Cell: (props) => {
      const battery = props.row.original.battery;
      return (
        <small>
          {battery == undefined ? (
            <BatteryHalfIcon color="#888888" size={"1.3em"} />
          ) : battery > 60 ? (
            <BatteryFullIcon color="#19b7bf" size={"1.3em"} />
          ) : battery < 30 ? (
            <BatteryIcon color="#CD3100" size={"1.3em"} />
          ) : battery >= 30 && props.row.original.battery <= 60 ? (
            <BatteryHalfIcon color="#FEC400" size={"1.3em"} />
          ) : (
            <BatteryFullIcon color="#888888" size={"1.3em"} />
          )}
          &nbsp; {battery ? `${battery} %` : "?"}
        </small>
      );
    },
  };
}

export function keyHeader(t) {
  return {
    // Header: `${t("hardware_serial_imei")}`,
    Header: (
      <Header
        name={`${t("hardware_serial_imei")}`}
        infoTitle={t("hardware_serial_imei")}
        infoContent={[t("hardware_serial_imei_description")]}
      />
    ),
    accessor: "hardware_serial",
    Cell: (props) => {
      const hardware_serial = props.row.original.hardware_serial;
      const imei = props.row.original.imei;
      const network_type = props.row.original.network_type;
      if (network_type == "NB") {
        return imei;
      } else {
        return hardware_serial;
      }
    },
  };
}

export default function SensorList({
  sensors,
  setOpenForm,
  setIsUpdate,
  setDetail,
  page,
  setPage,
  pageSize,
  setPageSize,
  search,
  setSearch,
  totalCount,
  hasNext,
  hasPrevious,
  isLoading,
  buttons,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const query = new URLSearchParams(location.search);
  const id = query.get("selectedSensor");
  const [searchInput, setSearchInput] = useState(search);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only update if search actually changed
      if (searchInput !== search) {
        setSearch(searchInput);
        setPage(1); // Reset to first page on search
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput, search, setSearch, setPage]);

  const handleEdit = (sensorDetail) => {
    setIsUpdate(true);
    setOpenForm(true);
    setDetail(sensorDetail);
  };
  const { mutateAsync: sensorDelete } = useMutation(deleteSensor, {
    onSuccess: () => {
      queryClient.invalidateQueries("sensors");
      queryClient.invalidateQueries(["sensordetail", id]);
    },
  });

  const handleDelete = (id) => {
    Swal.fire({
      html: '<p className="text-center"> ' + "Are you sure you want to delete " + "?" + "</p>",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#388090",
      cancelButtonColor: "#EF3600",
      cancelButtonText: "Cancel",
      confirmButtonText: "Delete",
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire("Deleted!", "Your file has been deleted.", "success");
        sensorDelete(id);
      }
    });
  };

  const columns = useMemo(
    () => [
      statusHeader(t),
      keyHeader(t),

      {
        Header: `${t("name")}`,
        accessor: "name",
      },
      // healthHeader(t),
      // {
      //   Header: `${t("threshold")}`,
      //   accessor: "threshold",
      // },
      batteryHeader(t),
      {
        Header: `${t("actions")}`,
        accessor: "actions",
        style: { width: "80px", textAlign: "center" },
        disableSortBy: true,
        Cell: (props) => {
          return (
            <div className="text-center">
              <span title="Edit">
                <EditIcon
                  onClick={() => {
                    handleEdit(props.row.original);
                  }}
                />
              </span>
              <span title={t("delete")}>
                <TrashIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    let id = props.row.original.id;
                    handleDelete(id);
                  }}
                />
              </span>
            </div>
          );
        },
      },
    ],
    []
  );
  const onRowClicked = (row) => {
    const { id } = row.original;
    navigate(`/sensors/list?selectedSensor=${id}`);
  };

  const handlePageChange = React.useCallback(
    (newPage) => {
      setPage(newPage);
    },
    [setPage, page]
  );

  const handlePageSizeChange = React.useCallback(
    (newPageSize) => {
      setPageSize(newPageSize);
      setPage(1); // Reset to first page when changing page size
    },
    [setPageSize, setPage]
  );

  return (
    <>
      <div className="mb-3">
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <Form.Control
            type="text"
            placeholder={t("search_sensors")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ flex: "1 1 300px", minWidth: "200px" }}
          />
          <Form.Select
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            style={{ width: "auto", minWidth: "80px" }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Form.Select>
          {buttons}
        </div>
        {search && (
          <small className="text-muted mt-1 d-block">
            {t("showing_results", { count: totalCount })}
          </small>
        )}
      </div>
      <ReactTable
        columns={columns}
        data={sensors}
        onRowClicked={onRowClicked}
        activeId={id}
        pagesize={pageSize}
        filter={false}
        manualPagination
        pageCount={Math.ceil(totalCount / pageSize)}
        currentPage={page}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        isLoading={isLoading}
      />
    </>
  );
}

export const SensorStatus = ({ color, title }) => {
  return (
    <span
      className={"badge"}
      style={{ padding: "5px", borderRadius: "2px", background: color }}
      title={`${title}`}
    >
      <div
        className="rounded-circle"
        style={{
          width: "6px",
          height: "6px",
          background: "#fff",
        }}
      ></div>
    </span>
  );
};
